import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  acceptRunningBuildConfig,
  getConfigDeployStatus,
  type ConfigStoreBindingStatus,
  type ConfigStoreBuildStatus,
} from '../../api/configStoreClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';

const useStyles = makeStyles({
  list: { margin: `${tokens.spacingVerticalS} 0`, paddingLeft: tokens.spacingHorizontalXL },
  actions: { marginTop: tokens.spacingVerticalS },
});

function describeLayers(binding: ConfigStoreBindingStatus): string {
  const changed = binding.layers.filter((l) => l.differs).map((l) => l.layer);
  return changed.length > 0 ? `differs in: ${changed.join(', ')}` : 'identical to this build\'s files';
}

/**
 * Shown when the config store was seeded by a NEWER build than the one serving this admin session — typically
 * after an older build was swapped back into production. The running build keeps the newer documents on its
 * own (seeding only moves forward); this lets an admin deliberately make THIS build's shipped files
 * authoritative. The current documents are backed up server-side first, and the server pins the rollback so
 * stamps still on the newer build cannot flip the store back.
 */
export function RunningBuildBanner({ onAccepted }: { onAccepted?: () => void }) {
  const styles = useStyles();
  const getAuthArgs = useAuthArgs();
  const [status, setStatus] = useState<ConfigStoreBuildStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const auth = await getAuthArgs();
      if (!auth) return;
      setStatus(await getConfigDeployStatus(auth));
    } catch {
      // The banner is advisory: a failed status read must never block the editor.
      setStatus(null);
    }
  }, [getAuthArgs]);

  useEffect(() => { void refresh(); }, [refresh]);

  const accept = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to manage configuration.');
        return;
      }
      const eligible = (status?.bindings ?? []).filter((b) => b.canAccept).map((b) => b.binding);
      const results = await acceptRunningBuildConfig(auth, eligible);
      const failed = results.filter((r) => !r.accepted);
      const accepted = results.filter((r) => r.accepted);
      setOpen(false);
      setDone(
        `Reset ${accepted.length} configuration area(s) to build ${status?.runningVersion}.` +
          (failed.length > 0 ? ` Not changed: ${failed.map((f) => `${f.binding} (${f.reason})`).join(', ')}.` : ''),
      );
      await refresh();
      onAccepted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [getAuthArgs, onAccepted, refresh, status]);

  const eligible = (status?.bindings ?? []).filter((b) => b.canAccept);

  return (
    <>
      {done && (
        <MessageBar intent="success" style={{ marginBottom: 12 }}>
          <MessageBarBody>{done}</MessageBarBody>
        </MessageBar>
      )}
      {eligible.length > 0 && (
        <MessageBar intent="warning" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <Text weight="semibold">This build is older than the configuration in the store.</Text>{' '}
            Build <strong>{status?.runningVersion}</strong> is serving this session, but {eligible.length} configuration
            area(s) were last written by a newer build, so they are being kept as-is (for example after an older build was
            swapped back into production). If this build is meant to stay in production, you can make its own
            configuration files authoritative.
            <div className={styles.actions}>
              <Button appearance="primary" onClick={() => setOpen(true)}>
                Review and use this build&apos;s configuration
              </Button>
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      <Dialog open={open} onOpenChange={(_, d) => { if (!busy) setOpen(d.open); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Use this build&apos;s configuration?</DialogTitle>
            <DialogContent>
              <Text>
                This replaces the stored configuration below with the files shipped in build{' '}
                <strong>{status?.runningVersion}</strong>, including any edits made in this editor. The current documents
                are saved to a backup first, and builds newer than this one will not overwrite it again until a newer
                release is deployed.
              </Text>
              <ul className={styles.list}>
                {eligible.map((b) => (
                  <li key={b.binding}>
                    <strong>{b.binding}</strong> — written by build {b.markerVersion ?? 'unknown'}; {describeLayers(b)}
                  </li>
                ))}
              </ul>
              <Text size={200}>
                Other stamps still running the newer build are unaffected until they are rolled back too.
              </Text>
              {error && (
                <MessageBar intent="error" style={{ marginTop: 8 }}>
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
              <Button appearance="primary" disabled={busy} onClick={() => void accept()}>
                {busy ? <Spinner size="tiny" /> : 'Replace with this build\'s configuration'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
